// API Service for Main App (Customer & Restaurant)
// Priority:
// 1) REACT_APP_API_URL (recommended)
// 2) Local dev fallback (localhost)
// 3) Production fallback
const getDefaultApiBaseUrl = () => {
  try {
    const host = window.location.hostname;
    const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
    if (isLocalhost) return 'http://localhost:5000/api';
  } catch {
    // window not available (tests/build tools)
  }

  return 'https://backend.menubazzar.com/api';
};

const API_BASE_URL = process.env.REACT_APP_API_URL || getDefaultApiBaseUrl();

// Helper function to get restaurant ID from URL or localStorage
const getRestaurantIdFromContext = () => {
  // Check if restaurant ID is in URL params (for customer app legacy routes)
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const restaurantId = urlParams.get('restaurantId');
    if (restaurantId) return restaurantId;
  } catch {
    // ignore
  }

  // Check if restaurantId is part of the pathname: /menu/:restaurantId/... (universal routes)
  const match = String(window.location.pathname || '').match(/\/menu\/([^/?#]+)/);
  if (match?.[1]) return match[1];

  // Check localStorage (for restaurant app)
  return localStorage.getItem('restaurantId');
};

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }

  try {
    // Ensure endpoint starts with / if API_BASE_URL doesn't end with /
    const url = API_BASE_URL.endsWith('/') 
      ? `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint.slice(1) : endpoint}`
      : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    
    const response = await fetch(url, config);
    
    // Handle non-JSON responses
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      throw new Error(text || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.ok) {
      const err = new Error(data.message || `API request failed: ${response.status} ${response.statusText}`);
      err.status = response.status;
      err.response = data;
      throw err;
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    // Provide more helpful error messages
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      throw new Error('Unable to connect to server. Please check your internet connection and ensure the backend is running.');
    }
    throw error;
  }
};

// Menu Items APIs (Public - for customer app)
export const menuItemsAPI = {
  getAll: async (restaurantId = null) => {
    const id = restaurantId || getRestaurantIdFromContext();
    const params = id ? `?restaurantId=${id}` : '';
    return apiRequest(`/menu-items${params}`);
  },

  getByCategory: async (category, restaurantId = null) => {
    const id = restaurantId || getRestaurantIdFromContext();
    const params = id ? `?restaurantId=${id}` : '';
    return apiRequest(`/menu-items/category/${category}${params}`);
  },

  getById: async (id) => {
    return apiRequest(`/menu-items/${id}`);
  },
};

// Orders APIs (Public - for customer app)
export const ordersAPI = {
  create: async (orderData) => {
    return apiRequest('/orders', {
      method: 'POST',
      body: orderData,
    });
  },
};

// Waiter call API (Public - for customer app)
export const waiterAPI = {
  callWaiter: async ({ restaurantId, tableNumber = null }) => {
    return apiRequest('/waiter-calls', {
      method: 'POST',
      body: { restaurantId, tableNumber },
    });
  },
};

// Categories API
export const categoriesAPI = {
  getAll: async (restaurantId = null) => {
    const id = restaurantId || getRestaurantIdFromContext();
    const params = id ? `?restaurantId=${id}` : '';
    return apiRequest(`/categories${params}`);
  },
};

// Restaurant API (Public)
export const restaurantAPI = {
  getById: async (id) => {
    return apiRequest(`/restaurants/${id}`);
  },
  getWifiStatus: async (id) => {
    return apiRequest(`/restaurants/${id}/wifi-status`);
  }
};

